import { useQueryClient } from '@tanstack/react-query';

import {
    AddProductBody,
    BaseProduct,
    Cart,
    CartEntryType,
    EntryWithDevice,
    EntryWithUpsell,
    ProductDeviceInCart,
    ProductVariant,
} from '@telialt/models/src/EshopModel';
import { selectFirstPurchasable, selectIsCartFresh } from '@telialt/utils/src/eshopSelectors';
import { isEntryWithDevice } from '@telialt/utils/src/eshopTypeGuards';
import { findLeafByCode, toPreparedVariations } from '@telialt/utils/src/eshopUtils';
import { useSegment } from '@telialt/utils/src/segment';

import { updateCart } from '../../helpers/cartQueryUtils';
import { ESHOP_MUTATION_KEY, ESHOP_QUERY_KEY } from '../../model';
import { apiAddProduct } from '../../store/api';
import { useCartQuery } from '../queries/useCartQuery';
import { resetToPreviousCartState, useMutationWithCartRefresh } from './useMutationWithCartRefresh';

type PurchasableProductMutation = {
    code: string;
    entryType: CartEntryType.PurchasableProduct;
    quantity?: number;
    product?: undefined;
};
type UpsellProductMutation = {
    code: string;
    entryType: CartEntryType.UpsellProduct;
    quantity?: number;
    product?: ProductDeviceInCart;
};

const predictEntryWithDevice = (
    code: ProductVariant['code'],
    cartCache: Cart,
    baseProduct: BaseProduct,
): EntryWithDevice => {
    const preparedVariation = findLeafByCode(toPreparedVariations(baseProduct.variantMatrix), code);
    const entry = selectFirstPurchasable(cartCache);
    const newColor = preparedVariation.parentsKey || preparedVariation.key;

    return {
        ...entry,
        product: {
            ...entry?.product,
            productColor: { code: 'unknown', name: newColor },
            storageSize: preparedVariation.key + 'GB',
            code,
            scalaId: code.replace(/_/g, ' '),
        },
    };
};

const predictEntryWithUpsell = (product: ProductDeviceInCart, cartCache: Cart) => {
    const lastEntryNumber = cartCache.entries.filter((entry) => entry.entryNumber != null)?.pop()?.entryNumber;

    return {
        entryNumber: (lastEntryNumber ?? 0) + 1,
        quantity: 1,
        product,
        entryType: CartEntryType.UpsellProduct,
    };
};

export const useAddProductMutation = () => {
    const segment = useSegment();
    const { cartId } = useCartQuery();
    const client = useQueryClient();

    const { mutate, mutateAsync, isPending, error } = useMutationWithCartRefresh(
        async ({ code, entryType, quantity }: PurchasableProductMutation | UpsellProductMutation) => {
            const body: AddProductBody = { product: { code }, entryType, quantity: quantity ?? 1 };

            return (await apiAddProduct(segment, { cartId }, body)).data.cart;
        },
        {
            onMutate: async ({ code, entryType, product }) => {
                const cartCache = client.getQueryData<Cart>([ESHOP_QUERY_KEY.CART]);
                const baseProduct = client.getQueryData<BaseProduct>([ESHOP_QUERY_KEY.BASE_PRODUCT]);
                if (selectIsCartFresh(cartCache)) {
                    return undefined;
                }

                let newCart: Cart = cartCache;

                if (entryType === CartEntryType.PurchasableProduct && !!baseProduct) {
                    const entryWithDevice = predictEntryWithDevice(code, cartCache, baseProduct);
                    newCart = {
                        ...cartCache,
                        entries: [
                            ...(cartCache?.entries.filter((entry) => !isEntryWithDevice(entry)) ?? []),
                            entryWithDevice,
                        ],
                    };
                }

                if (entryType === CartEntryType.UpsellProduct && !!product) {
                    const entryWithUpsellProduct = predictEntryWithUpsell(product, cartCache) as EntryWithUpsell;
                    newCart = {
                        ...cartCache,
                        entries: [...cartCache?.entries, entryWithUpsellProduct],
                    };
                }

                updateCart(client, newCart);

                return cartCache;
            },
            onError: resetToPreviousCartState(client),
            mutationKey: [ESHOP_MUTATION_KEY.ADD_CART_ENTRY],
        },
    );

    return {
        addProductAsync: mutateAsync,
        addProduct: mutate,
        addProductStatusIsLoading: isPending,
        addProductError: error,
    };
};
